import { useCallback, useEffect, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #65 — Brand-scoped pending changes. Reads /api/brand/:slug/changes
// (session-cookie auth), the pending-suggestion slice of tenant_change_log.
// Read-only: the brand router exposes no accept/dismiss endpoint, so this lists
// suggestions awaiting an upload or admin action rather than acting on them.

interface ChangeRow {
  id: string;
  tenant_id: string;
  tenant_store_id: string | null;
  source: string;
  change_type: string;
  changed_at: string;
  source_external_id: string | null;
  display_name_at_event: string | null;
  previous_identity: Record<string, unknown> | null;
  new_identity: Record<string, unknown> | null;
  changes: Array<{ field?: string; old?: unknown; new?: unknown }> | null;
  is_suggestion: boolean;
  suggestion_state: string | null;
  notes: string | null;
  store_display_name: string | null;
}

const CHANGE_TYPE_COLORS: Record<string, string> = {
  created: "bg-green-100 text-green-800 border-green-200",
  reappeared: "bg-emerald-100 text-emerald-800 border-emerald-200",
  unarchived: "bg-emerald-100 text-emerald-800 border-emerald-200",
  renamed: "bg-amber-100 text-amber-800 border-amber-200",
  removed: "bg-red-100 text-red-800 border-red-200",
  archived: "bg-gray-200 text-gray-700 border-gray-300",
  merged: "bg-purple-100 text-purple-800 border-purple-200",
  chain_changed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  address_changed: "bg-blue-100 text-blue-800 border-blue-200",
};

const SOURCE_BADGE: Record<string, string> = {
  wtb: "bg-sky-100 text-sky-800 border-sky-200",
  cm: "bg-violet-100 text-violet-800 border-violet-200",
  gbp: "bg-orange-100 text-orange-800 border-orange-200",
  manual: "bg-gray-100 text-gray-700 border-gray-200",
  system: "bg-gray-100 text-gray-700 border-gray-200",
};

function changeTypeColor(t: string): string {
  return CHANGE_TYPE_COLORS[t] || "bg-gray-100 text-gray-700 border-gray-200";
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(d);
  return `${t} · ${day}`;
}

function changeSnippet(r: ChangeRow): string | null {
  if (r.changes && r.changes.length > 0) {
    const c = r.changes[0];
    if (c.field === "display_name") return `was: ${String(c.old ?? "—")}`;
    if (c.field) return `${c.field}: ${String(c.old ?? "—")} → ${String(c.new ?? "—")}`;
  }
  const prev = r.previous_identity as { display_name?: string } | null;
  if (prev?.display_name) return `was: ${prev.display_name}`;
  if (r.notes) return r.notes;
  return null;
}

export default function BrandChanges({ slug }: { slug: string }) {
  const toast = useToast();
  const base = `/api/brand/${encodeURIComponent(slug)}/changes`;

  const [rows, setRows] = useState<ChangeRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(base);
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load changes: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [base, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <a href={`#/brand/${encodeURIComponent(slug)}/my-chains`} className="text-xs text-gray-500 hover:text-gray-800">
            ← My Chains
          </a>
          <h2 className="mt-1 text-2xl font-semibold text-gray-900">Pending changes</h2>
          <p className="text-sm text-gray-600 mt-1">
            Suggested changes awaiting your next upload. See <a href={`#/brand/${encodeURIComponent(slug)}/activity`} className="text-accent-700 hover:underline">Activity</a> for the full history.
          </p>
        </div>
        <Button variant="ghost" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">✓</div>
            <div className="text-sm font-medium text-gray-800">Nothing pending.</div>
            <div className="text-xs text-gray-500 mt-1">You're all caught up — no suggested changes to review.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Store</th>
                <th className="text-left px-4 py-2 font-medium">Change</th>
                <th className="text-left px-4 py-2 font-medium">Detail</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const name = r.store_display_name || r.display_name_at_event || "—";
                return (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-2 whitespace-nowrap" title={r.changed_at}>
                      {formatWhen(r.changed_at)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{name}</div>
                      {r.source_external_id && (
                        <div className="text-xs text-gray-400 font-mono">{r.source_external_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${changeTypeColor(r.change_type)}`}>
                        {r.change_type}
                      </span>
                      <span className="ml-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px]">
                        suggestion
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{changeSnippet(r) || "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase ${SOURCE_BADGE[r.source] || SOURCE_BADGE.system}`}>
                        {r.source}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <div>{rows.length > 0 ? `${rows.length} pending` : "0 pending"}</div>
        </div>
      </div>
    </div>
  );
}
